import allocateManagementApi, {
  AllocationPayload,
  VirtualWarehouseFilter,
  VirtualWarehouseItem,
  VirtualWarehouseListParams,
} from "@api/allocateManagementApi";
import CustomError from "@api/utils/CustomeError";
import { translateErrors } from "@utils/translateError";

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { message } from "antd";

import type { RootState } from "../rootReducer";

interface IState {
  isFetching: boolean;
  isFetchingSubmit: boolean;
  isFetchingImport: boolean;
  isFetchingDestination: boolean;
  importSuccess: boolean;
  importErrorResults?: string[];
  list: {
    count: number;
    next: string | null;
    previous: string | null;
    results: VirtualWarehouseItem[];
  };
  filter: VirtualWarehouseFilter;
  destinationAllocatableMap: Record<string, number>;
}

const initialState: IState = {
  isFetching: false,
  isFetchingSubmit: false,
  isFetchingImport: false,
  isFetchingDestination: false,
  importSuccess: false,
  importErrorResults: undefined,
  list: {
    count: 0,
    next: null,
    previous: null,
    results: [],
  },
  filter: {
    sku: undefined,
    regionType: undefined,
    retailerId: undefined,
    productName: undefined,
    effectiveDateAfter: undefined,
    effectiveDateBefore: undefined,
    batch: undefined,
    limit: 20,
    offset: 0,
  },
  destinationAllocatableMap: {},
};

const buildVirtualWarehouseListParams = ({
  sku,
  regionType,
  retailerId,
  ...rest
}: VirtualWarehouseFilter): VirtualWarehouseListParams => ({
  ...rest,
  sku: sku && sku.length > 0 ? sku.join(",") : undefined,
  regionType: regionType ?? "",
  retailerId: retailerId || "",
});

export const submitBatchAllocation = createAsyncThunk(
  "virtualWarehouse/submitBatchAllocation",
  async (payloads: AllocationPayload[]) => {
    await allocateManagementApi.submitAllocation(payloads);
  },
);

export const fetchVirtualWarehouseList = createAsyncThunk(
  "virtualWarehouse/fetchVirtualWarehouseList",
  async (params: VirtualWarehouseFilter) => {
    const response = await allocateManagementApi.fetchVirtualWarehouseList(buildVirtualWarehouseListParams(params));
    return response;
  },
);

export const fetchDestinationAllocatableQty = createAsyncThunk(
  "virtualWarehouse/fetchDestinationAllocatableQty",
  async ({ retailerId, sku }: { retailerId: number; sku: string[] }) => {
    const requestParams = buildVirtualWarehouseListParams({ retailerId, sku, limit: 100, offset: 0 });
    const response = await allocateManagementApi.fetchVirtualWarehouseList(requestParams);
    const map: Record<string, number> = {};
    response.results.forEach((item) => {
      map[`${retailerId}:${item.sku}`] = item.canUseQty;
    });
    return map;
  },
);

export const importAllocateOrder = createAsyncThunk<void, File, { state: RootState }>(
  "virtualWarehouse/importAllocateOrder",
  async (file, thunkApi) => {
    try {
      await allocateManagementApi.importAllocateOrder(file);
      message.success("匯入成功");
      thunkApi.dispatch(fetchVirtualWarehouseList(thunkApi.getState().virtualWarehouseSlice.filter));
      return undefined;
    } catch (error) {
      if (error instanceof CustomError && error.result) {
        thunkApi.dispatch(updateImportErrorResults(translateErrors(error.result)));
      }
      return thunkApi.rejectWithValue(error);
    }
  },
);

const virtualWarehouseSlice = createSlice({
  name: "virtualWarehouse",
  initialState,
  reducers: {
    setVirtualWarehouseFilter: (state, action) => {
      state.filter = action.payload;
    },
    clearImportSuccess: (state) => {
      state.importSuccess = false;
    },
    updateImportErrorResults: (state, action) => {
      state.importErrorResults = action.payload;
    },
    clearDestinationData: (state) => {
      state.destinationAllocatableMap = {};
    },
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchVirtualWarehouseList.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(fetchVirtualWarehouseList.fulfilled, (state, action) => {
      state.isFetching = false;
      state.list = action.payload;
    });
    builder.addCase(fetchVirtualWarehouseList.rejected, (state) => {
      state.isFetching = false;
    });
    builder.addCase(submitBatchAllocation.pending, (state) => {
      state.isFetchingSubmit = true;
    });
    builder.addCase(submitBatchAllocation.fulfilled, (state) => {
      state.isFetchingSubmit = false;
    });
    builder.addCase(submitBatchAllocation.rejected, (state) => {
      state.isFetchingSubmit = false;
    });
    builder.addCase(fetchDestinationAllocatableQty.pending, (state) => {
      state.isFetchingDestination = true;
    });
    builder.addCase(fetchDestinationAllocatableQty.fulfilled, (state, action) => {
      state.isFetchingDestination = false;
      state.destinationAllocatableMap = { ...state.destinationAllocatableMap, ...action.payload };
    });
    builder.addCase(fetchDestinationAllocatableQty.rejected, (state) => {
      state.isFetchingDestination = false;
    });
    builder.addCase(importAllocateOrder.pending, (state) => {
      state.isFetchingImport = true;
    });
    builder.addCase(importAllocateOrder.fulfilled, (state) => {
      state.isFetchingImport = false;
      state.importSuccess = true;
    });
    builder.addCase(importAllocateOrder.rejected, (state) => {
      state.isFetchingImport = false;
    });
  },
});

export const virtualWarehouseState = (state: RootState) => state.virtualWarehouseSlice;
export const {
  setVirtualWarehouseFilter,
  clearImportSuccess,
  updateImportErrorResults,
  clearDestinationData,
  reset: resetVirtualWarehouse,
} = virtualWarehouseSlice.actions;
export default virtualWarehouseSlice.reducer;
